import type { SVGProps } from 'react';

import type { IconName } from '@/types';

/**
 * Single inline-SVG icon set. Paths are drawn on a 24x24 grid and inherit
 * `currentColor`, so icons colour themselves from their parent.
 *
 * `filled` marks the glyphs that read better as solid shapes (brand marks and
 * store badges) than as 1.8px strokes.
 */
interface IconDefinition {
  path: string;
  filled?: boolean;
}

const ICONS: Record<IconName, IconDefinition> = {
  alert: { path: 'M12 3.5 21.5 20h-19z M12 10v4 M12 17.2v.01' },
  'arrow-right': { path: 'M5 12h14M13 6l6 6-6 6' },
  chart: { path: 'M4 20h16 M7 20V11 M12 20V5 M17 20v-6' },
  check: { path: 'M4 12.5 9 17.5 20 6.5' },
  'chevron-down': { path: 'M6 9l6 6 6-6' },
  close: { path: 'M6 6l12 12M18 6L6 18' },
  fingerprint: {
    path: 'M12 4a7 7 0 0 0-7 7v2.5 M19 12v-1a7 7 0 0 0-3.2-5.9 M8.5 19.6A9 9 0 0 0 9 16v-4a3 3 0 0 1 6 0v4c0 1.2-.2 2.4-.6 3.5 M12 12v4.2 M5.2 17.4c.4-1 .6-2 .6-3',
  },
  'id-card': {
    path: 'M3 6h18v12H3z M8.5 12.2a1.9 1.9 0 1 0 0-3.8 1.9 1.9 0 0 0 0 3.8z M5.4 15.6c.5-1.3 1.7-2.1 3.1-2.1s2.6.8 3.1 2.1 M14.5 10h4 M14.5 13.5h4',
  },
  invoice: {
    path: 'M6 3h9l3 3v15l-2.2-1.3L13.6 21l-2.2-1.3L9.2 21 7 19.7 4.8 21V5a2 2 0 0 1 2-2z M8.5 9h7 M8.5 12.5h7 M8.5 16h4',
  },
  layers: { path: 'M12 3.5 21 8l-9 4.5L3 8z M3.5 12.5 12 16.8l8.5-4.3 M3.5 16.5 12 20.8l8.5-4.3' },
  mail: {
    path: 'M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z M3.5 7.5 12 13l8.5-5.5',
  },
  'map-pin': {
    path: 'M12 21s7-5.3 7-11a7 7 0 1 0-14 0c0 5.7 7 11 7 11z M12 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
  },
  menu: { path: 'M4 7h16M4 12h16M4 17h16' },
  phone: {
    path: 'M4 5c0-.6.4-1 1-1h3l2 5-2.2 1.3a12 12 0 0 0 5.9 5.9L15 14l5 2v3c0 .6-.4 1-1 1A15 15 0 0 1 4 5z',
  },
  shield: { path: 'M12 3l7 3v6c0 4.4-3 7.9-7 9-4-1.1-7-4.6-7-9V6z M9 12l2 2 4-4' },
  store: {
    path: 'M4 9.5 5.5 4.5h13L20 9.5 M4 9.5h16v10H4z M4 9.5a2.2 2.2 0 0 0 4 0 2.2 2.2 0 0 0 4 0 2.2 2.2 0 0 0 4 0 2.2 2.2 0 0 0 4 0 M9.5 19.5v-5h5v5',
  },
  wallet: {
    path: 'M4 7.5A2.5 2.5 0 0 1 6.5 5H18v2.5 M3.5 8h16A1.5 1.5 0 0 1 21 9.5v8a1.5 1.5 0 0 1-1.5 1.5H5a1.5 1.5 0 0 1-1.5-1.5z M17 13.5v.01',
  },
};

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  /** Rendered size in pixels (width and height). */
  size?: number;
  /** Provide when the icon is the only content of a control. */
  title?: string;
}

export function Icon({ name, size = 20, title, ...props }: IconProps) {
  const icon = ICONS[name];

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={icon.filled ? 'currentColor' : 'none'}
      stroke={icon.filled ? 'none' : 'currentColor'}
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title ? 'img' : 'presentation'}
      aria-hidden={title ? undefined : true}
      focusable="false"
      {...props}
    >
      {title ? <title>{title}</title> : null}
      <path d={icon.path} />
    </svg>
  );
}
